import { z } from 'zod';
import { KPI_AGGREGATIONS, KPI_DIRECTIONS, KPI_PERIODICITIES } from './kpis';

/**
 * Metas dos KPIs (T-062, RF-23, PRD §11.12): por tipo de período, ano e período, opcionalmente por
 * unidade e por área. Cada célula tem uma versão vigente; revisar cria nova versão com justificativa e
 * a anterior continua consultável (DEC-004 item 10).
 */
export const TARGET_PERIOD_TYPES = ['MONTH', 'QUARTER', 'YEAR', 'ACCUMULATED'] as const;
export type TargetPeriodTypeName = (typeof TARGET_PERIOD_TYPES)[number];
export const TARGET_PERIOD_TYPE_LABELS: Record<TargetPeriodTypeName, string> = {
  MONTH: 'Mensal',
  QUARTER: 'Trimestral',
  YEAR: 'Anual',
  ACCUMULATED: 'Acumulada (até o mês)',
};
/** Quantos períodos o tipo tem no ano (mês 1–12, trimestre 1–4, ano = 1, acumulada até o mês 1–12). */
export const TARGET_PERIOD_COUNT: Record<TargetPeriodTypeName, number> = {
  MONTH: 12,
  QUARTER: 4,
  YEAR: 1,
  ACCUMULATED: 12,
};
const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
/** Rótulo curto do período ("Mar", "2º tri", "Ano", "Até Mar"). */
export function targetPeriodLabel(type: TargetPeriodTypeName, period: number): string {
  if (type === 'MONTH') return MONTHS[period - 1] ?? String(period);
  if (type === 'QUARTER') return `${period}º tri`;
  if (type === 'YEAR') return 'Ano';
  return `Até ${MONTHS[period - 1] ?? period}`;
}

/** Valor de meta: cabe na coluna DECIMAL(20,6) (menos de 10^14, até 6 casas). */
export const targetValueSchema = z.coerce
  .number({ message: 'Informe a meta em números.' })
  .finite('Informe a meta em números.')
  .refine((v) => Math.abs(v) < 1e14, 'Número grande demais (até 99 trilhões).')
  .refine((v) => Number.isInteger(Math.round(v * 1e6 * 1e3) / 1e3), 'Use no máximo seis casas decimais.');

/** Estado da versão no fluxo de aprovação (T-063, RF-24). Sem o fluxo, toda versão nasce aprovada. */
export const TARGET_APPROVAL_STATUSES = ['APPROVED', 'PENDING', 'REJECTED'] as const;
export type TargetApprovalStatusName = (typeof TARGET_APPROVAL_STATUSES)[number];
export const TARGET_APPROVAL_LABELS: Record<TargetApprovalStatusName, string> = {
  APPROVED: 'Aprovada',
  PENDING: 'Pendente de aprovação',
  REJECTED: 'Rejeitada',
};

/** Uma célula da grade (versão vigente). value null = a meta foi removida (há histórico). */
export const targetCellSchema = z.object({
  id: z.string().uuid(),
  kpiId: z.string().uuid(),
  periodType: z.enum(TARGET_PERIOD_TYPES),
  year: z.number().int(),
  period: z.number().int(),
  businessUnitId: z.string().uuid().nullable(),
  departmentId: z.string().uuid().nullable(),
  value: z.number().nullable(),
  version: z.number().int(),
  updatedAt: z.string(),
  /** Estado da versão vigente; pendente ou rejeitada não vale no cálculo. */
  approvalStatus: z.enum(TARGET_APPROVAL_STATUSES),
  /** Meta em vigor (a última versão aprovada; null = sem meta aprovada). */
  approvedValue: z.number().nullable(),
  /** Comentário de quem rejeitou (rejeitada) ou aprovou. */
  decisionComment: z.string().nullable(),
});
export type TargetCell = z.infer<typeof targetCellSchema>;

/** KPI na grade de metas (o necessário para mostrar e calcular o acumulado). */
export const targetKpiSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  perspective: z.object({ id: z.string().uuid(), name: z.string() }),
  unitLabel: z.string(),
  periodicity: z.enum(KPI_PERIODICITIES),
  direction: z.enum(KPI_DIRECTIONS),
  aggregation: z.enum(KPI_AGGREGATIONS),
});
export type TargetKpi = z.infer<typeof targetKpiSchema>;

export const targetGridSchema = z.object({
  year: z.number().int(),
  periodType: z.enum(TARGET_PERIOD_TYPES),
  kpis: z.array(targetKpiSchema),
  cells: z.array(targetCellSchema),
  /** Meses fechados do ano (T-066): gravar meta que fecha num deles exige justificativa (RN-06). */
  closedMonths: z.array(z.number().int()),
});
export type TargetGrid = z.infer<typeof targetGridSchema>;

export const targetGridQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  periodType: z.enum(TARGET_PERIOD_TYPES).default('MONTH'),
  businessUnitId: z.string().uuid().optional(),
  departmentId: z.string().uuid().optional(),
  kpiId: z.string().uuid().optional(),
});

/** Célula a gravar: valor null = remover a meta vigente (vira nova versão "sem meta", com justificativa). */
export const targetWriteSchema = z
  .object({
    kpiId: z.string().uuid(),
    periodType: z.enum(TARGET_PERIOD_TYPES),
    year: z.number().int().min(2000).max(2100),
    period: z.number().int().min(1),
    businessUnitId: z.string().uuid().nullable().default(null),
    departmentId: z.string().uuid().nullable().default(null),
    value: targetValueSchema.nullable(),
  })
  .refine((c) => c.period <= TARGET_PERIOD_COUNT[c.periodType], {
    message: 'Período fora do ano para esse tipo de meta.',
    path: ['period'],
  });
export type TargetWrite = z.input<typeof targetWriteSchema>;

export const targetBatchSchema = z.object({
  cells: z
    .array(targetWriteSchema)
    .min(1, 'Nada para gravar.')
    .max(500, 'No máximo 500 metas por vez.')
    .refine(
      (cells) =>
        new Set(
          cells.map((c) =>
            [c.kpiId, c.periodType, c.year, c.period, c.businessUnitId ?? '', c.departmentId ?? ''].join('|'),
          ),
        ).size === cells.length,
      'A mesma meta aparece duas vezes.',
    ),
  /** Justificativa: obrigatória quando alguma meta existente muda ou é removida (revisão). */
  reason: z
    .string()
    .trim()
    .max(1000, 'A justificativa pode ter no máximo 1000 caracteres.')
    .nullable()
    .optional()
    .transform((v) => v || null),
});
export type TargetBatch = z.input<typeof targetBatchSchema>;

export const targetBatchResultSchema = z.object({
  created: z.number().int(),
  revised: z.number().int(),
  removed: z.number().int(),
  unchanged: z.number().int(),
  /** As versões gravadas ficaram pendentes de aprovação (fluxo ligado no momento da gravação). */
  pendingApproval: z.boolean(),
  cells: z.array(targetCellSchema),
});
export type TargetBatchResult = z.infer<typeof targetBatchResultSchema>;

export const targetCopySchema = z
  .object({
    fromYear: z.number().int().min(2000).max(2100),
    toYear: z.number().int().min(2000).max(2100),
    periodTypes: z
      .array(z.enum(TARGET_PERIOD_TYPES))
      .min(1)
      .default([...TARGET_PERIOD_TYPES]),
    kpiIds: z.array(z.string().uuid()).max(500).optional(),
    /** Só um recorte (geral: os dois null; unidade; ou área). Sem ele, copia todos os recortes. */
    breakdown: z
      .object({
        businessUnitId: z.string().uuid().nullable(),
        departmentId: z.string().uuid().nullable(),
      })
      .refine((b) => !(b.businessUnitId && b.departmentId), 'Escolha unidade ou área, não as duas.')
      .optional(),
  })
  .refine((v) => v.fromYear !== v.toYear, { message: 'Escolha anos diferentes.', path: ['toYear'] });
export type TargetCopy = z.input<typeof targetCopySchema>;
/** Regras de metas da empresa (T-063): fluxo formal de aprovação (padrão desligado — DEC-004 item 8). */
export const targetSettingsSchema = z.object({ approvalRequired: z.boolean() });
export type TargetSettings = z.infer<typeof targetSettingsSchema>;
export const DEFAULT_TARGET_SETTINGS: TargetSettings = { approvalRequired: false };

/** Versão pendente de aprovação (painel de quem aprova). */
export const targetPendingSchema = z.object({
  id: z.string().uuid(),
  kpi: z.object({ id: z.string().uuid(), code: z.string(), name: z.string(), unitLabel: z.string() }),
  periodType: z.enum(TARGET_PERIOD_TYPES),
  year: z.number().int(),
  period: z.number().int(),
  businessUnit: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  department: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  /** Valor proposto (null = propõe remover a meta). */
  value: z.number().nullable(),
  /** Meta em vigor hoje (a última aprovada). */
  approvedValue: z.number().nullable(),
  version: z.number().int(),
  reason: z.string().nullable(),
  createdByName: z.string(),
  createdAt: z.string(),
});
export type TargetPending = z.infer<typeof targetPendingSchema>;
/** Pendências (as mais antigas primeiro, até 500 por vez) e o total no escopo. */
export const targetPendingListSchema = z.object({
  total: z.number().int(),
  items: z.array(targetPendingSchema),
});

export const TARGET_DECISIONS = ['APPROVE', 'REJECT'] as const;
/** Aprovar ou rejeitar versões pendentes (em lote); rejeitar exige comentário. */
export const targetDecisionSchema = z
  .object({
    ids: z
      .array(z.string().uuid())
      .min(1, 'Escolha ao menos uma meta.')
      .max(500, 'No máximo 500 metas por vez.')
      .refine((ids) => new Set(ids).size === ids.length, 'A mesma meta aparece duas vezes.'),
    decision: z.enum(TARGET_DECISIONS),
    comment: z
      .string()
      .trim()
      .max(1000, 'O comentário pode ter no máximo 1000 caracteres.')
      .nullable()
      .optional()
      .transform((v) => v || null),
  })
  .refine((v) => v.decision === 'APPROVE' || (v.comment !== null && v.comment.length >= 3), {
    message: 'Explique o motivo da rejeição.',
    path: ['comment'],
  });
export type TargetDecision = z.input<typeof targetDecisionSchema>;
export const targetDecisionResultSchema = z.object({
  approved: z.number().int(),
  rejected: z.number().int(),
});

/**
 * existing = o destino já tinha meta (mantida); removed = no destino a meta foi removida de propósito (fica
 * sem meta); archived = unidade ou área arquivada (não copiada).
 */
export const targetCopyResultSchema = z.object({
  copied: z.number().int(),
  existing: z.number().int(),
  removed: z.number().int(),
  archived: z.number().int(),
  /** Não copiadas porque o mês está fechado (T-066): entram pela grade, com justificativa. */
  closed: z.number().int(),
});

/** Versão de uma meta (histórico da célula). value null = meta removida nessa versão. */
export const targetVersionSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  value: z.number().nullable(),
  reason: z.string().nullable(),
  createdByName: z.string(),
  createdAt: z.string(),
  current: z.boolean(),
  approvalStatus: z.enum(TARGET_APPROVAL_STATUSES),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionComment: z.string().nullable(),
});
export const targetHistorySchema = z.object({ items: z.array(targetVersionSchema) });
export const targetHistoryQuerySchema = z.object({
  kpiId: z.string().uuid(),
  periodType: z.enum(TARGET_PERIOD_TYPES),
  year: z.coerce.number().int(),
  period: z.coerce.number().int().min(1),
  businessUnitId: z.string().uuid().optional(),
  departmentId: z.string().uuid().optional(),
});

/** Opções da tela: anos do horizonte, unidades e áreas (no escopo de quem pede). */
export const targetOptionsSchema = z.object({
  years: z.array(z.number().int()),
  businessUnits: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  departments: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});

// ---------------------------------------------------------------------------------------------
// Distribuição da meta anual em mensais

export const DISTRIBUTION_MODES = ['EQUAL', 'PROPORTIONAL'] as const;
export type DistributionModeName = (typeof DISTRIBUTION_MODES)[number];

/**
 * Distribui a meta anual em `count` partes (12 meses): igual, ou proporcional a uma base (ex.: as metas
 * mensais do ano anterior, para manter a sazonalidade). Arredonda em `decimals` casas e acerta a
 * diferença na parte de maior peso (na igual, a última), para a soma bater exatamente com o total —
 * mês sem base fica com zero. Base sem nada a ponderar (vazia, zerada ou negativa) cai na igual.
 */
export function distributeAnnual(
  total: number,
  mode: DistributionModeName,
  basis: (number | null)[] = [],
  decimals = 2,
  count = 12,
): number[] {
  const factor = 10 ** decimals;
  const weights =
    mode === 'PROPORTIONAL' && basis.length === count && basis.every((b) => b === null || b >= 0)
      ? basis.map((b) => b ?? 0)
      : [];
  const sum = weights.reduce((s, w) => s + w, 0);
  const shares = sum > 0 ? weights.map((w) => w / sum) : Array.from({ length: count }, () => 1 / count);
  const parts = shares.map((s) => Math.round(total * s * factor) / factor);
  const diff = Math.round((total - parts.reduce((s, p) => s + p, 0)) * factor) / factor;
  // A maior parte (a última, em caso de empate) absorve a diferença do arredondamento.
  const target = shares.reduce((best, s, i) => (s >= shares[best]! ? i : best), 0);
  parts[target] = Math.round((parts[target]! + diff) * factor) / factor;
  return parts;
}
